import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ProjectDto } from '@app/admin/models/project-dto';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { convertToUTC } from '@admin/pages/projects/util/date-helper';
import { buildProjectForm } from '@admin/pages/projects/util/project-form-builder';
import { Store } from '@ngrx/store';
import { LoadProject, LoadProjects } from '@dwo/actions';
import { getCurrentProject } from '@app/core/store/selectors/project.selector';
import { takeUntil } from 'rxjs/operators';
import { ProjectService } from '@app/core/services/project.service';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
  standalone: false,
})
export class EditProjectComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private projectId: string;
  private loadedProject: ProjectDto | null = null;

  form: UntypedFormGroup;
  readonly divisionTypes: string[] = ['OFFSHORE', 'FABRICATION'];
  readonly completionSystems: string[] = ['COMPLETION', 'PROCOSYS', 'PIMS'];
  readonly originTypes: string[] = ['MIPS'];
  readonly progressCutOffDay: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store,
    private readonly projectService: ProjectService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    if (this.projectId) {
      this.store.dispatch(new LoadProject(this.projectId));
    }

    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project: ProjectDto) => {
        if (project && project.id === this.projectId) {
          this.loadedProject = project;
          if (this.form && this.form.dirty) {
            return;
          }
          this.form = buildProjectForm(this.formBuilder, project);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onReset(): void {
    this.form = null;
    this.store.dispatch(new LoadProject(this.projectId));
  }

  onSave(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const data = { ...this.form.getRawValue() } as ProjectDto;

    const jobControl = this.form.get('jobFeedbackRecipientEmail');
    const weightControl = this.form.get('weightChangeRecipientEmail');

    const jobValue = `${jobControl?.value ?? ''}`.trim();
    const weightValue = `${weightControl?.value ?? ''}`.trim();

    if (jobValue) {
      data.jobFeedbackRecipientEmail = jobValue;
    } else if (jobControl?.dirty) {
      data.jobFeedbackRecipientEmail = null as any;
    } else if (this.loadedProject?.jobFeedbackRecipientEmail) {
      data.jobFeedbackRecipientEmail = this.loadedProject.jobFeedbackRecipientEmail;
    } else {
      delete data.jobFeedbackRecipientEmail;
    }

    if (weightValue) {
      data.weightChangeRecipientEmail = weightValue;
    } else if (weightControl?.dirty) {
      data.weightChangeRecipientEmail = null as any;
    } else if (this.loadedProject?.weightChangeRecipientEmail) {
      data.weightChangeRecipientEmail = this.loadedProject.weightChangeRecipientEmail;
    } else {
      delete data.weightChangeRecipientEmail;
    }

    if (data.progressCutOffTime) {
      data.progressCutOffTime = convertToUTC(data.progressCutOffTime);
    }

    this.projectService.updateProject(data).subscribe({
      next: () => {
        this.form.markAsPristine();
        this.store.dispatch(new LoadProjects());
        this.store.dispatch(new LoadProject(this.projectId));
      },
      error: (err) => {
        if (err?.url?.includes(`/admin/projects/${this.projectId}`)) {
          // Backend returns 303 See Other on success; the browser follows the
          // redirect to GET /admin/projects/{id} which may return 401.
          // The update was already performed — treat as success.
          this.form.markAsPristine();
          this.store.dispatch(new LoadProjects());
          this.store.dispatch(new LoadProject(this.projectId));
          return;
        }
        console.error('Error updating project:', err);
      },
    });
  }

  onClose(): void {
    this.router.navigateByUrl('/admin/projects');
  }
}
